import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { getMonthlyAllocations } from "../../http/requests/project-management";
import { toastOptions } from "../../common/utils/toasterOptions";
import "./MonthlyAllocationsModal.css";

const formatDate = (value) => {
  if (!value) return "";
  return dayjs(value).format("DD MMM YYYY");
};

const normalizeRow = (row) => ({
  projectName: row?.projectName ?? row?.ProjectName ?? "",
  name: row?.name ?? row?.Name ?? "",
  startDate: row?.startDate ?? row?.StartDate,
  endDate: row?.endDate ?? row?.EndDate,
  billAllocation: row?.billAllocation ?? row?.BillAllocation ?? 0,
});

const buildMonthOptions = () => {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonthIndex = now.month();
  const options = [];

  for (let monthIndex = 0; monthIndex < currentMonthIndex; monthIndex += 1) {
    const cursor = dayjs().year(currentYear).month(monthIndex).startOf("month");
    options.push({
      value: cursor.format("YYYY-MM-01"),
      label: cursor.format("MMMM YYYY"),
    });
  }

  return options.reverse();
};

const getDefaultMonth = (options) => (options.length > 0 ? options[0].value : "");

const MonthlyAllocationsModal = ({ open, onClose, projectId, projectName }) => {
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(() => getDefaultMonth(buildMonthOptions()));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedMonthLabel = useMemo(() => {
    const match = monthOptions.find((m) => m.value === selectedMonth);
    return match?.label ?? dayjs(selectedMonth).format("MMMM YYYY");
  }, [monthOptions, selectedMonth]);

  const fetchData = useCallback(async () => {
    if (!projectId || !open || !selectedMonth) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getMonthlyAllocations(projectId, selectedMonth);
      const list = response?.data ?? response?.Data ?? [];
      setRows(Array.isArray(list) ? list.map(normalizeRow) : []);
    } catch (err) {
      const message =
        err?.response?.data?.errorMessage ||
        err?.message ||
        "Failed to load monthly allocations.";
      setError(message);
      setRows([]);
      toast.error(message, toastOptions);
    } finally {
      setLoading(false);
    }
  }, [open, projectId, selectedMonth]);

  useEffect(() => {
    if (open) {
      const defaultMonth = getDefaultMonth(monthOptions);
      setSelectedMonth(defaultMonth);
    } else {
      setRows([]);
      setError(null);
    }
  }, [open, monthOptions]);

  useEffect(() => {
    if (open && selectedMonth) {
      fetchData();
    }
  }, [open, selectedMonth, fetchData]);

  const handleDownloadExcel = () => {
    if (!rows.length) {
      toast.info("No data to export.", toastOptions);
      return;
    }
    const sheetData = rows.map((r) => ({
      "Project Name": r.projectName,
      Name: r.name,
      "Start Date": formatDate(r.startDate),
      "End Date": formatDate(r.endDate),
      "Bill Allocation": r.billAllocation,
    }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Allocations History");
    const safeProject = (projectName || "Project").replace(/[^\w\-]+/g, "_");
    const safeMonth = selectedMonthLabel.replace(/\s+/g, "_");
    XLSX.writeFile(wb, `${safeProject}_Allocations_History_${safeMonth}.xlsx`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="monthly-allocations-modal">
      <DialogTitle className="monthly-allocations-modal__title">
        View Allocations History
        {projectName ? (
          <Typography variant="body2" className="monthly-allocations-modal__subtitle">
            {projectName}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Box className="monthly-allocations-modal__toolbar">
          {monthOptions.length > 0 ? (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="monthly-allocation-month-label">Month</InputLabel>
              <Select
                labelId="monthly-allocation-month-label"
                label="Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {monthOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No past months available for {dayjs().year()}.
            </Typography>
          )}
          <Button
            variant="contained"
            color="success"
            onClick={handleDownloadExcel}
            disabled={loading || !rows.length}
          >
            Download Excel
          </Button>
        </Box>

        {loading ? (
          <Box className="monthly-allocations-modal__center">
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Loading allocations...
            </Typography>
          </Box>
        ) : error ? (
          <Box className="monthly-allocations-modal__center">
            <Typography color="error">{error}</Typography>
            <Button variant="outlined" onClick={fetchData} sx={{ mt: 1 }}>
              Retry
            </Button>
          </Box>
        ) : rows.length === 0 ? (
          <Box className="monthly-allocations-modal__center">
            <Typography color="textSecondary">
              No allocation records found for {selectedMonthLabel}.
            </Typography>
          </Box>
        ) : (
          <TableContainer className="monthly-allocations-modal__table-wrap">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="right">Bill Allocation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.name}-${index}`}>
                    <TableCell>{row.projectName}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{formatDate(row.startDate)}</TableCell>
                    <TableCell>{formatDate(row.endDate)}</TableCell>
                    <TableCell align="right">{row.billAllocation}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonthlyAllocationsModal;
